import dayjs from 'dayjs';
import { Fragment, useEffect, useState } from 'react';

import { Carousel } from '@mantine/carousel';
import { Box, Divider, Flex, LoadingOverlay, ScrollArea, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';

import { useQueryClient } from '@tanstack/react-query';

import useAppStore from '@/shared/store/app.store';
import useUserStore from '@/shared/store/user.store.ts';
import { IObject } from '@/shared/types/bookingObjects.types';
import { IPersonalPlaceRelease, TPersonalPlaceActive } from '@/shared/types/personalPlaces.types';
import { BottomSheet } from '@/shared/uikit/components/bottomSheet';
import { SliderButton } from '@/shared/uikit/components/button/sliderButton';
import { ModalSheet } from '@/shared/uikit/components/modalSheet';
import { IconicRow } from '@/shared/uikit/components/rows/IconicRow';
import { LayoutRow } from '@/shared/uikit/layouts/LayoutRow';

import { PersonalPlaceDetail } from './PersonalPlaceDetail';

type PersonalPlacesListProps = {
	open?: boolean;
	close?: () => void;
};

type TPersonalPlacesType = 'all' | 'work_place' | 'parking_place' | 'work_park';

const buttonList: { label: string; type: TPersonalPlacesType }[] = [
	{
		label: 'Все',
		type: 'all',
	},
	{
		label: 'Рабочее место',
		type: 'work_place',
	},
	{
		label: 'Парковка',
		type: 'parking_place',
	},
	// {
	// 	label: 'Рабочее место и парковка',
	// 	type: 'work_park',
	// },
];

export const PersonalPlacesList = ({ open, close }: PersonalPlacesListProps) => {
	const queryClient = useQueryClient();
	const isMobile = useAppStore((state) => state.isMobile);
	const personalPlaces = useUserStore((state) => state.personalPlaces);
	const [places, setPlaces] = useState<IObject[]>([]);
	const [filter, setFilter] = useState<TPersonalPlacesType>('work_place');
	const [selectedPlace, setSelectedPlace] = useState<IObject & TPersonalPlaceActive>();

	const getPersonalPlaces = queryClient.getQueryData(['personalPlaces']) as IPersonalPlaceRelease[];

	const [openedPlaceDetail, { close: closePlaceDetail, open: openPlaceDetail }] =
		useDisclosure(false);

	const handleFilterPlaces = (filter: TPersonalPlacesType) => {
		setFilter(filter);
		switch (filter) {
			case 'all':
				return setPlaces(personalPlaces);

			case 'work_place':
				return setPlaces(personalPlaces.filter((object) => object.type.kind === filter));

			case 'parking_place':
				return setPlaces(personalPlaces.filter((object) => object.type.kind === filter));
		}
	};

	const handleOpenPlaceDetail = (place: IObject, state: TPersonalPlaceActive) => {
		setSelectedPlace({ ...place, ...state });
		openPlaceDetail();
	};

	useEffect(() => {
		handleFilterPlaces('work_place');
	}, []);

	const content = (
		<Box pos="relative">
			<LoadingOverlay
				visible={!personalPlaces}
				zIndex={1000}
				overlayProps={{ radius: 2, blur: 2 }}
			/>
			<Flex direction="column" mt={16}>
				{places &&
					places?.map((place) => {
						const activeState = getPersonalPlaces?.find(
							(state) => state.booking_object_id === place.id,
						);

						return (
							<Fragment key={place.id}>
								<IconicRow
									mainText={place?.name?.replace(/\s\|\s[\d\D]+/gm, '')}
									dimmedText={place.description}
									onClick={() => {
										handleOpenPlaceDetail(place, {
											start: dayjs(activeState?.start).toDate() ?? null,
											finish: dayjs(activeState?.finish).toDate() ?? null,
											release_id: activeState?.id ?? null,
											active: !!activeState,
										});
									}}
									{...(activeState && {
										rightContentSlot: (
											<Text c="dimmed" size="sm" fw={500}>
												Свободный доступ
											</Text>
										),
									})}
									clickable
								/>
								{personalPlaces[personalPlaces.length - 1] !== place && (
									<Divider my={8} color="#6670851A" />
								)}
							</Fragment>
						);
					})}
			</Flex>
			{!places && (
				<Flex direction="column" align="center" justify="center" h={'calc(100vh - 300px)'}>
					<Text fz={18} fw={700}>
						Пока ничего нет 😔
					</Text>
				</Flex>
			)}
		</Box>
	);

	if (isMobile)
		return (
			<>
				<BottomSheet
					opened={open!}
					onClose={close!}
					trapFocus={false}
					title="Личные места"
					navbar={
						<Carousel
							withControls={false}
							slidesToScroll="auto"
							slideSize="auto"
							slideGap={8}
							align="start"
						>
							{buttonList &&
								buttonList?.map((el) => (
									<Carousel.Slide key={el.type}>
										<SliderButton
											active={filter !== el.type}
											onClick={() => handleFilterPlaces(el.type)}
										>
											{el.label}
										</SliderButton>
									</Carousel.Slide>
								))}
						</Carousel>
					}
					withCloseButton={false}
					withBackButton
					isFullScreen
				>
					{content}
				</BottomSheet>

				{openedPlaceDetail && selectedPlace && (
					<BottomSheet
						opened={openedPlaceDetail}
						onClose={closePlaceDetail}
						title={selectedPlace?.name}
						withBackButton={false}
						isFullScreen
					>
						<PersonalPlaceDetail object={selectedPlace} />
					</BottomSheet>
				)}
			</>
		);

	return (
		<>
			<LayoutRow
				title={{
					title: { children: 'Личные места', size: 'h4', fw: 800 },
				}}
				type="once"
				style={{ height: 'calc(100vh - 116px)' }}
			>
				<Carousel
					withControls={false}
					slidesToScroll="auto"
					slideSize="auto"
					slideGap={8}
					align="start"
				>
					{buttonList &&
						buttonList?.map((el) => (
							<Carousel.Slide key={el.type}>
								<SliderButton
									active={filter !== el.type}
									onClick={() => handleFilterPlaces(el.type)}
								>
									{el.label}
								</SliderButton>
							</Carousel.Slide>
						))}
				</Carousel>
				<ScrollArea h={600} scrollbarSize={4} offsetScrollbars>
					{content}
				</ScrollArea>
			</LayoutRow>

			{openedPlaceDetail && selectedPlace && (
				<ModalSheet
					opened={openedPlaceDetail}
					onClose={closePlaceDetail}
					title={selectedPlace?.name}
					size="lg"
				>
					<PersonalPlaceDetail object={selectedPlace} />
				</ModalSheet>
			)}
		</>
	);
};
